import React from "react";
import { useQuery } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";

import DocumentMarkdownView from "@/components/common/DocumentMarkdownView";
import { BASE_API_URL } from "@/constants";
import fpFetch from "@/services/fpFetch";

type Props = { fileId: string };

/**
 * Viewer for an uploaded Markdown (.md) document (#11065).
 *
 * Unlike the HTML viewer there is no derivative to fetch: the stored bytes are
 * already the markdown the pipeline extracted text from, so what the reader
 * sees is exactly what the LLM consumed.
 */
const MarkdownViewer = ({ fileId }: Props) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["markdown-blob", fileId],
    queryFn: async () => {
      const response = await fpFetch(
        `${BASE_API_URL}/userData/fp0065?fileId=${fileId}&v=cacheBust8`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to load markdown content: ${response.status} ${response.statusText}`
        );
      }
      return await response.text();
    },
    enabled: Boolean(fileId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <CircularProgress size={32} />
      </div>
    );
  }
  if (error || data == null) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-semibold">
            Unable to load markdown content
          </div>
        </div>
      </div>
    );
  }

  return <DocumentMarkdownView markdown={data} />;
};

export default MarkdownViewer;
